import type { PointerEventHandler, PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, type LinkProps, useNavigation } from 'react-router';
import cn from '@/utils/merge-class-names';

type SharedProps = {
  icon?: ReactNode;
  progressIcon?: ReactNode;
  label?: string;
  isLoading?: boolean;
  className?: string;
  disabled?: boolean;
  tabIndex?: number;
  showLabelWhileLoading?: boolean;
};

type Props =
  | (SharedProps & {
      as: 'button' | 'submit';
      onClick?: () => void;
      onPointerDown?: PointerEventHandler<HTMLButtonElement>;
      onMouseDown?: PointerEventHandler<HTMLButtonElement>;
    })
  | (SharedProps & {
      as: 'link';
      to: LinkProps['to'];
    });

export default function ProgressButton({
  icon = null,
  progressIcon,
  className,
  disabled,
  isLoading,
  label,
  children,
  showLabelWhileLoading = false,
  ...otherProps
}: PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const { state } = useNavigation();

  const isAnyLoading = isLoading || state === 'loading';

  const content = (
    <>
      {isAnyLoading ? (progressIcon ?? <span className='loading loading-spinner loading-sm' />) : icon}
      {(showLabelWhileLoading || !isAnyLoading) && (children ?? (label ? t(label) : null))}
    </>
  );

  if (otherProps.as === 'link') {
    return (
      <Link
        to={otherProps.to}
        className={cn('btn', { 'btn-disabled pointer-events-none': disabled || isAnyLoading }, className)}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={otherProps.as}
      disabled={disabled || isAnyLoading}
      onPointerDown={otherProps.onPointerDown}
      onMouseDown={otherProps.onMouseDown}
      onClick={otherProps.onClick}
      className={cn('btn whitespace-nowrap', { 'btn-disabled': disabled }, className)}>
      {content}
    </button>
  );
}
